import { memo, useMemo } from "react";
import { StyledActionButton, StyledActionLink } from "./index.styled";
import { FormattedMessage, useIntl } from "react-intl";
import {
  FaArrowLeft,
  FaCheck,
  FaChevronRight,
  FaChevronUp,
  FaCopy,
  FaDownload,
  FaEdit,
  FaEye,
  FaHistory,
  FaMinus,
  FaPlus,
  FaSave,
  FaSync,
  FaTimes,
  FaTrash,
  FaUndo,
} from "react-icons/fa";
import { Popconfirm, Tooltip } from "antd";
import { IoMoveOutline } from "react-icons/io5";
import { AiFillThunderbolt } from "react-icons/ai";
import { TbWorldSearch } from "react-icons/tb";
import _ from "lodash";
import { UseQueryProps } from "@lib/types";
import FromNow from "@lib/components/MiniComponents/LiveDate/FromNow";

export type ActionButtonType =
  | "add"
  | "minus"
  | "edit"
  | "delete"
  | "close"
  | "save"
  | "cancel"
  | "back"
  | "reset"
  | "submit"
  | "move"
  | "refresh"
  | "detail"
  | "quickLook"
  | "scan"
  | "history"
  | "link"
  | "showMore"
  | "blockChain"
  | "copy"
  | "download"
  | "other";

type ActionButtonProps = {
  type?: ActionButtonType;
  targetBlank?: boolean;
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
  color?: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  href?: string;
  loading?: boolean;
  disabled?: boolean;
  label?: string | React.ReactNode;
  block?: boolean;
  style?: React.CSSProperties;
  noTooltip?: boolean;
  tooltipPosition?: "top" | "bottom" | "left" | "right";
  noIcon?: boolean;
  noLabel?: boolean;
  noBg?: boolean;
  showBorder?: boolean;
  itemName?: string;
  size?: "small" | "medium" | "large";
  showMoreProps?: {
    moreText?: string;
    lessText?: string;
    moreIcon?: React.ReactNode;
    lessIcon?: React.ReactNode;
    iconPosition?: "left" | "right";
    state?: boolean;
  };
} & (
  | {
      type: "delete";
      itemName: string;
    }
  | {
      type: Exclude<ActionButtonType, "delete">;
      itemName?: string;
    }
  | {
      type: "link";
      icon?: React.ReactNode;
      color?: string;
      href: string;
    }
  | {
      type: "refresh";
      query?: UseQueryProps<any>;
    }
);

function ActionButton(props: ActionButtonProps) {
  const { messages } = useIntl();
  const {
    type,
    targetBlank = false,
    icon,
    iconPosition = "left",
    color,
    onClick,
    href,
    loading,
    disabled,
    label,
    noLabel,
    block,
    style,
    noTooltip,
    tooltipPosition,
    noIcon,
    noBg,
    itemName,
    showBorder,
    size = "medium",
  } = props;

  const showMoreProps = useMemo(() => {
    if (type !== "showMore") return null;
    return {
      moreText: messages["common.showMore"] as string,
      lessText: messages["common.showLess"] as string,
      moreIcon: <FaChevronRight />,
      lessIcon: <FaChevronUp />,
      iconPosition: "right",
      state: false,
      ...props?.showMoreProps,
    };
  }, [messages, props?.showMoreProps, type]);

  const isPopConfirm = useMemo(() => {
    return type === "delete";
  }, [type]);

  const isSubmit = useMemo(() => {
    return (
      type === "submit" || type === "save" || type === "add" || type === "edit"
    );
  }, [type]);

  const BtnIcon = useMemo(() => {
    switch (type) {
      case "add":
        return <FaPlus />;
      case "minus":
        return <FaMinus />;
      case "edit":
        return <FaEdit />;
      case "delete":
        return <FaTrash />;
      case "close":
        return <FaTimes />;
      case "save":
        return <FaSave />;
      case "cancel":
        return <FaTimes />;
      case "back":
        return <FaArrowLeft />;
      case "reset":
        return <FaUndo />;
      case "submit":
        return <FaCheck />;
      case "move":
        return <IoMoveOutline />;
      case "refresh":
        return <FaSync />;
      case "detail":
        return <FaEye />;
      case "scan":
        return <AiFillThunderbolt />;
      case "history":
        return <FaHistory />;
      case "blockChain":
        return <TbWorldSearch />;
      case "showMore":
        return showMoreProps?.state
          ? showMoreProps.lessIcon
          : showMoreProps.moreIcon;
      case "copy":
        return <FaCopy />;
      case "download":
        return <FaDownload />;
    }
  }, [type, showMoreProps]);

  const BtnText = useMemo(() => {
    if (label) return label;

    switch (type) {
      case "add":
        return messages["common.add"] as string;
      case "minus":
        return messages["common.minus"] as string;
      case "edit":
        return messages["common.edit"] as string;
      case "delete":
        return messages["common.delete"] as string;
      case "close":
        return messages["common.close"] as string;
      case "save":
        return messages["common.save"] as string;
      case "cancel":
        return messages["common.cancel"] as string;
      case "reset":
        return messages["common.reset"] as string;
      case "submit":
        return messages["common.submit"] as string;
      case "refresh":
        return messages["common.refresh"] as string;
      case "detail":
        return messages["common.detail"] as string;
      case "quickLook":
        return messages["common.quickLook"] as string;
      case "scan":
        return messages["common.scanRequest"] as string;
      case "blockChain":
        return messages["common.blockChain"] as string;
      case "history":
        return messages["transaction_history"] as string;
      case "showMore":
        return showMoreProps?.state
          ? showMoreProps.lessText
          : showMoreProps.moreText;
      case "download":
        return messages["common.download"] as string;
    }
  }, [type, label, showMoreProps]);

  const BtnTooltipLabel = useMemo(() => {
    if (type === "refresh") {
      return (
        <FromNow
          date={(props as any)?.query?.dataUpdatedAt}
          beforeDate={`${messages["common.lastUpdate"] as string} : `}
        />
      );
    }

    return BtnText;
  }, [type, (props as any)?.query?.dataUpdatedAt, messages]);

  const BtnContainer = ({ children }: { children: React.ReactNode }) => {
    if (noTooltip) return children;
    if (type === "showMore") return children;

    return (
      <Tooltip title={BtnTooltipLabel} placement={tooltipPosition}>
        <div>{children}</div>
      </Tooltip>
    );
  };

  const BtnPopConfirm = ({ children }: { children: React.ReactNode }) => {
    if (!isPopConfirm) return children;

    return (
      <Popconfirm
        title={
          <FormattedMessage
            id={"common.confirm.delete.title"}
            values={{ name: (messages[itemName] as string) || itemName }}
          />
        }
        description={
          <FormattedMessage
            id={"common.confirm.delete.message"}
            values={{ name: (messages[itemName] as string) || itemName }}
          />
        }
        onConfirm={onClick}
        okText={messages["common.yes"] as string}
        cancelText={messages["common.no"] as string}
      >
        <div>{children}</div>
      </Popconfirm>
    );
  };

  const _icon = noIcon ? null : icon || BtnIcon;

  return (
    <BtnContainer>
      <BtnPopConfirm>
        {href ? (
          <StyledActionLink
            to={href}
            target={targetBlank ? "_blank" : undefined}
            style={{ display: "flex", alignItems: "center", ...style }}
            actionType={type}
            noBg={noBg}
            loading={loading}
            disabled={loading || disabled}
            color={color}
            showBorder={showBorder}
            size={size}
          >
            {iconPosition === "left" && _icon}
            {(!noLabel || !_icon) && BtnText}
            {iconPosition === "right" && _icon}
          </StyledActionLink>
        ) : (
          <StyledActionButton
            icon={_icon}
            iconPosition={iconPosition === "left" ? "start" : "end"}
            htmlType={isSubmit ? "submit" : "button"}
            onClick={!isPopConfirm ? onClick : undefined}
            loading={loading}
            disabled={loading || disabled}
            actionType={type}
            block={block}
            style={style}
            noBg={noBg}
            showBorder={showBorder}
            size={size}
          >
            {(!noLabel || !_icon) && BtnText}
          </StyledActionButton>
        )}
      </BtnPopConfirm>
    </BtnContainer>
  );
}

export default memo(ActionButton, (prevProps, nextProps) => {
  return _.isEqual(prevProps, nextProps);
});
